import React, { useState, useEffect, useCallback } from "react";
import { getLastWPPosts } from "../services/posts";
import { RESTPost } from "../services/types";
import Header from "../components/Header";
import BDPost from "../components/BDPost";
import AdSense from "../components/AdSense";
import PostSkeleton from "../components/PostSkeleton";
import Button from "../components/Button";
import {useParams} from "react-router-dom";

const AuthorPage : React.FC = () => {
    const { authorId } = useParams();
    const [posts, setPosts] = useState<RESTPost[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadPosts = useCallback(async (page: number, append: boolean = false) => {
        if (!authorId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await getLastWPPosts(page, authorId);
            
            if (append) {
                setPosts(prevPosts => [...prevPosts, ...response.posts]);
            } else {
                setPosts(response.posts);
            }
            
            setTotalPages(response.totalPages);
        } catch (err) {
            setError('Failed to load posts');
            console.error('Error loading posts:', err);
        } finally {
            setLoading(false);
        }
    }, [authorId]);

    const loadMorePosts = useCallback(() => {
        if (currentPage < totalPages && !loading) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            loadPosts(nextPage, true);
        }
    }, [currentPage, totalPages, loading, loadPosts]);

    useEffect(() => {
        if (authorId) {
            setCurrentPage(1);
            loadPosts(1);
        }
    }, [authorId, loadPosts]);

    const hasMorePosts = currentPage < totalPages;

    return (
        <div className={"Page authorPage postsList"}>
            <Header/>
            {loading && posts.length === 0 ? (
                <PostSkeleton count={5} />
            ) : (
                posts.map((post: RESTPost, index: number) => (
                    <React.Fragment key={post.id}>
                        <BDPost post={post} />
                        {/* Insert ad after 1st, 4th, and 6th posts */}
                        {(index === 0 || index === 3 || index === 5) && (
                            <AdSense />
                        )}
                    </React.Fragment>
                ))
            )}
            
            {hasMorePosts && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Button 
                        variant="secondary" 
                        size="medium" 
                        onClick={loadMorePosts}
                        loading={loading}
                    >
                        {loading ? 'Chargement...' : 'Charger plus de BDs de cet auteur'}
                    </Button>
                </div>
            )}
            
            {error && (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
                    {error}
                </div>
            )}
            
            {!hasMorePosts && posts.length > 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    Vous avez vu toutes les BDs de cet auteur !
                </div>
            )}
            
            {posts.length === 0 && !loading && !error && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    Cet auteur n'a pas encore publié de BDs.
                </div>
            )}
        </div>
    )
}

export default AuthorPage;